import React, { useEffect, useState } from "react";
import {
    withStyles,
    Theme,
    createStyles,
    makeStyles,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    Grid,
    TextField,
    Box,
    Typography,
    DialogActions,
    Button,
    Switch,
    Chip,
} from "@material-ui/core";
import { Solvent } from "pages/statuses/Solvent";
import { ConfigGetter } from "utilities/ConfigGetter";
import * as ConfigKey from "utilities/ConfigGetter";
import * as DbValueConstants from "utilities/DbValueConstants";
import * as CommonConstants from "utilities/CommonConstants";
import BigNumber from "bignumber.js";
import { BookableSolventApi } from "api/BookableSolventApi";
import { DateUtil } from "utilities/DateUtil";
import { BookingApi } from "api/BookingApi";
import { EventType } from "pages/statuses/EventType";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        message: {
            fontSize: `small`,
        },
        solventItem: {
            fontSize: `small`,
        },
    })
);

interface Props {
    // 予約情報
    booking: EventType;
    // 予約可能溶媒
    bookableSolvent: Solvent[];
    // 予約可能溶媒セッター
    setBookableSolvent: any;
    // 登録溶媒表示ボタン削除
    buttonDelete: any;
    // 予約対象溶媒
    targetBookingSolvent: Solvent[];
    // 予約対象溶媒セッター
    setTargetBookingSolvent: any;
    //全ての溶媒情報
    allSolvents: Solvent[];
    // ローディング状態のセッター
    setLoading: any;
}

/**
 * 予約溶媒選択コンポーネント
 * 予約したい溶媒の名前・容量を選択する
 */
export const SelectSolventParts: React.FC<Props> = (props) => {
    const {
        booking,
        bookableSolvent,
        setBookableSolvent,
        buttonDelete,
        targetBookingSolvent,
        setTargetBookingSolvent,
        allSolvents,
        setLoading,
    } = props;
    const classes = useStyles();
    const [inputCapacity, setInputCapacity] = useState(``);
    const [inputUnit, setInputUnit] = useState(true);
    const [inViewSolventCapacity, setInViewSolventCapacity] = useState(false);
    const [selectSolvent, setSelectSolvent] = useState<Solvent>({
        id: 0,
        capacity: new BigNumber(0),
        capacityKg: new BigNumber(`selectSolvent.kgPerKan, selectSolvent.literPerKan`),
        unit: ``,
        name: ``,
        subName: ``,
        dailyLimit: 0,
        kgPerKan: new BigNumber(0),
        literPerKan: new BigNumber(0),
        specificGravity: new BigNumber(0),
        pump1Flag: false,
        pump2Flag: false,
        colorCode: `white`,
    });
    // エラーメッセージ
    const [errorMessage, setErrorMessage] = useState<string>(``);

    useEffect(() => {
        const execute = async () => {
            if (bookableSolvent.length > 0 || targetBookingSolvent.length > 0) {
                return;
            }
            // ローディング開始
            setLoading(true);

            // 予約可能な溶媒を取得
            let detail = await BookableSolventApi.getBookableSolvent(
                DateUtil.parseDate(booking.start),
                DateUtil.parseTime(booking.start),
                DateUtil.parseTime(booking.end),
                booking.bookingTypeId
            );
            if (detail?.status === 200) {
                const bookableSolvents: Solvent[] = Array.from(detail?.data.solvents);
                setBookableSolvent(bookableSolvents);

                // 休日以外の場合
                if (booking.groupId !== CommonConstants.GROUP_ID_HOLIDAY) {
                    if (booking.groupId === CommonConstants.GROUP_ID_FREE) {
                        // 新規登録
                        // 予約枠が空いているかどうかチェック
                        let result: any = await BookingApi.checkFree(
                            booking.start,
                            booking.end,
                            booking.bookingTypeId
                        );
                        if (!result.data?.success) {
                            if (result.data?.errorMessage !== null) {
                                setErrorMessage(result.data?.errorMessage);
                            } else {
                                setErrorMessage(result.errors?.errorMessage);
                            }
                            setLoading(false);
                            return;
                        }
                        setTargetBookingSolvent([]);
                    }
                }
            }

            // ローディング終了
           setLoading(false);
        };
        execute();
        // eslint-disable-next-line
    }, []);
    
    const solventCapacityClickOpen = (data: Solvent) => {
        setSelectSolvent(data);
        setBookableSolvent(bookableSolvent);

        setInputCapacity(``);

        // 初期表示は「L」
        setInputUnit(true);

        setInViewSolventCapacity(true);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // ジエチルエーテルまたはペンタンの場合は、Lのみ予約可能にするための処理
        if (
            selectSolvent.id !== DbValueConstants.SOLVENT_ID_DIETHYLETHER &&
            selectSolvent.id !== DbValueConstants.SOLVENT_ID_PENTANE
        ) {
            setInputUnit(event.target.checked);
        }
        // 切り替えた際、上限を超えていた場合、容量を調整
        const limit = new BigNumber(!inputUnit ? ConfigGetter.getNum(ConfigKey.KEY_SOLVENT_LIMIT_CAPACITY_L) : ConfigGetter.getNum(ConfigKey.KEY_SOLVENT_LIMIT_CAPACITY_KG));
        const capacity = new BigNumber(inputCapacity);
        if (capacity.gt(limit)) {
            setInputCapacity(limit.toString());
        }
    };

    const solventCapacityClose = () => {
        setInViewSolventCapacity(false);
    };

    const buttonAdd = (id: number) => {
        let solvent = allSolvents.find((solvent) => solvent.id === id);
        if (solvent === undefined) {
            return;
        }

        bookableSolvent.push(solvent);
        bookableSolvent.sort((a, b) => {
            if (a.id > b.id) {
                return 1;
            }
            if (a.id < b.id) {
                return -1;
            }
            return 0;
        });
    };

    const chipAdd = (capacity: string, unit: boolean) => () => {
        if (capacity === `` || capacity === `0`) {
            return;
        }
        const unitNumber = unit;
        if (unitNumber === false) {
            selectSolvent.unit = DbValueConstants.UNIT_ID_KG;
        } else {
            selectSolvent.unit = DbValueConstants.UNIT_ID_L;
        }

        targetBookingSolvent.push({
            id: selectSolvent.id,
            capacity: new BigNumber(capacity),
            capacityKg: selectSolvent.capacityKg,
            unit: selectSolvent.unit,
            name: selectSolvent.name,
            subName: selectSolvent.subName,
            dailyLimit: selectSolvent.dailyLimit,
            kgPerKan: selectSolvent.kgPerKan,
            literPerKan: selectSolvent.literPerKan,
            specificGravity: selectSolvent.specificGravity,
            pump1Flag: selectSolvent.pump1Flag,
            pump2Flag: selectSolvent.pump2Flag,
            colorCode: selectSolvent.colorCode,
        });

        buttonDelete(selectSolvent.id);

        setInViewSolventCapacity(false);
    };

    // 溶媒がいくつ選択されたか判定
    const checkSelectedSolventCount = () => {
        return targetBookingSolvent.length < ConfigGetter.getNum(ConfigKey.KEY_SOLVENT_LIMIT_COUNT);
    };

    const chipDelete = (chipToDelete: Solvent) => () => {
        setTargetBookingSolvent(chipToDelete);
        buttonAdd(chipToDelete.id);
    };

    const AntSwitch = withStyles((theme: Theme) =>
        createStyles({
            root: {
                width: 28,
                height: 16,
                padding: 0,
                display: `flex`,
            },
            switchBase: {
                padding: 2,
                color: theme.palette.common.white,
                "&$checked": {
                    transform: `translateX(12px)`,
                    color: theme.palette.common.white,
                    "& + $track": {
                        opacity: 1,
                        backgroundColor: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                    },
                },
            },
            thumb: {
                width: 12,
                height: 12,
                boxShadow: `none`,
            },
            track: {
                border: `1px solid ${theme.palette.secondary.main}`,
                borderRadius: 16 / 2,
                opacity: 1,
                backgroundColor: theme.palette.secondary.main,
            },
            checked: {},
        })
    )(Switch);

    /**
     * 溶媒名ボタンを押下したときに表示されるダイアログ（登録画面）
     */
    const solventCapacityElement = (
        <Dialog open={inViewSolventCapacity} onClose={solventCapacityClose} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">
                {selectSolvent.name}
                {selectSolvent.subName}　{selectSolvent.kgPerKan}kg={selectSolvent.literPerKan}L
            </DialogTitle>
            {bookableSolvent === null && setBookableSolvent(bookableSolvent)}
            <DialogContent>
                <DialogContentText></DialogContentText>
                <Grid container direction="row">
                    <TextField
                        autoFocus
                        margin="dense"
                        id="capacity"
                        label="容量"
                        type="number"
                        value={inputCapacity}
                        // defaultValue="0.0"
                        inputProps={{
                            step: 0.5,
                            min: 0.0,
                        }}
                        onChange={(e) => {
                            if (e.target.value === '') {
                                setInputCapacity(e.target.value);
                                return;
                            }
                            // Lとkgで上限が異なるので設定
                            let limit = new BigNumber(inputUnit
                                ? ConfigGetter.getNum(ConfigKey.KEY_SOLVENT_LIMIT_CAPACITY_L)
                                : ConfigGetter.getNum(ConfigKey.KEY_SOLVENT_LIMIT_CAPACITY_KG));
                                // 一日の上限が決まっている溶媒の場合はその上限を設定
                            limit = selectSolvent.dailyLimit ? new BigNumber(selectSolvent.dailyLimit) : limit;

                            // 上限を超えないよう入力値を調整
                            const inputValue = new BigNumber(e.target.value).dp(1, BigNumber.ROUND_DOWN);
                            if (inputValue.lte(limit) && inputValue.gte(0)) {
                                setInputCapacity(inputValue.toString());
                            }
                        }}
                    />
                    <Box mt={3} ml={3}>
                        <Typography component="div">
                            <Grid component="label" container alignItems="center" spacing={1}>
                                <Grid item>kg</Grid>
                                <Grid item>
                                    <AntSwitch checked={inputUnit} onChange={handleChange} name="checkedC" />
                                </Grid>
                                <Grid item>L</Grid>
                            </Grid>
                        </Typography>
                    </Box>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={solventCapacityClose} color="primary">
                    キャンセル
                </Button>
                <div>
                    {checkSelectedSolventCount() ? (
                        <Button onClick={chipAdd(inputCapacity, inputUnit)} color="primary">
                            OK
                        </Button>
                    ) : (
                        <Button color="secondary">
                            溶媒は{ConfigGetter.getNum(ConfigKey.KEY_SOLVENT_LIMIT_COUNT)}種類まで持ち出し可能です
                        </Button>
                    )}
                </div>
            </DialogActions>
        </Dialog>
    );

    return (
        <Grid container spacing={3} direction="row" justify="center" alignItems="stretch">
            <p className="error">{errorMessage}</p>
            {solventCapacityElement}
            <Grid item xs={12} sm={6}>
                <h6>以下の溶媒の中から選択してください。</h6>
                {bookableSolvent.map((data) => {
                    return (
                        <Box key={data.id} m={2}>
                            <Button
                                onClick={() => solventCapacityClickOpen(data)}
                                variant="contained"
                                style={{ background: data.colorCode }}
                                className={classes.solventItem}
                            >
                                {data.name} {data.subName}
                            </Button>
                        </Box>
                    );
                })}
            </Grid>
            <Grid item xs={12} sm={6}>
                <h6>選択中の溶媒</h6>
                {targetBookingSolvent.map((data) => {
                    return (
                        <Box key={data.id} m={2}>
                            <Chip
                                label={
                                    data.name +
                                    ` ` +
                                    data.subName +
                                    ` ／ ` +
                                    data.capacity +
                                    (data.unit === DbValueConstants.UNIT_ID_L
                                        ? CommonConstants.UNIT_NAME_L
                                        : CommonConstants.UNIT_NAME_KG)
                                }
                                onDelete={chipDelete(data)}
                                style={{ background: data.colorCode }}
                            />
                        </Box>
                    );
                })}
            </Grid>
        </Grid>
    );
};
